// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ApiService, BaseApiService } from '@fc-core/services/base-api.service';
import { EMPTY, Observable } from 'rxjs';
import { AnyVehicle, VehicleState } from '@fc-core/models';
import { expand, map, reduce } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Moment } from 'moment';
import { FCHttpParamEncoder } from '@fc-shared/utils/param.encoder';
import { BackendResponse } from '@fc-core/models/response.model';

export interface VehicleStateReading {
  created: Date;
  datetime: Date;
  status: number;
  trackerId: number;
  vehicleId: number;
  installId: number;
  vehicleStateId: number;
  packetId: number;
  code: number;
  heading: number;
  point: any;
  speed: string;
  altitude: number;
  engineLoad?: number | string;
  engine_load?: number | string;
  throttle?: number | string;
  throttlePosition?: number | string;
  throttle_position?: number | string;
  rpm?: number | string;
  engineRpm?: number | string;
  engine_rpm?: number | string;
  gForce?: number | string;
  g_force?: number | string;
  gforce?: number | string;
  acceleration?: number | string;
  /** Raw accelerometer axes in milli-g (Topfly); null when not reported. */
  accel?: {
    x: number | null;
    y: number | null;
    z: number | null;
  } | null;
  state: any;
  vehicleInfo: {
    engineLoad?: number | string;
    engine_load?: number | string;
    throttle?: number | string;
    throttlePosition?: number | string;
    throttle_position?: number | string;
    rpm?: number | string;
    engineRpm?: number | string;
    engine_rpm?: number | string;
    gForce?: number | string;
    g_force?: number | string;
    gforce?: number | string;
    acceleration?: number | string;
    [key: string]: unknown;
  };
}

export interface VehicleStatePath {
  type: 'Feature';
  geometry: {
    type: 'LineString';
    coordinates: [number, number][];
  };
  properties: {
    coordTimes: string[];
    headings: number[];
    speeds: number[];
    distanceMeters: number;
  };
}

export interface VehicleStateHistorgamData {
  idling: number;
  stopped: number;
  total: number;
  towed: number;
  traveling: number;
}

@Injectable({ providedIn: 'root' })
@ApiService({ baseName: 'vehicles-state' })
export class VehicleStateApiService extends BaseApiService {
  vehicleId: number;

  constructor(private http: HttpClient) {
    super();
  }

  static fromJSON(input: any): VehicleState {
    return <VehicleState>{
      ...input,
      modified: new Date(input.modified),
      created: new Date(input.created),
      duration: {
        lower: new Date(input.duration.lower),
        upper: new Date(input.duration.upper),
      },
    };
  }

  loadPage(
    vehicle: AnyVehicle,
    page: number,
    limit: number,
  ): Observable<VehicleState[]> {
    let offset = 0;

    if (page) {
      offset = page * limit - limit;
    }
    let params: HttpParams = new HttpParams().set('offset', offset.toString());

    if (limit) {
      params = params.set('limit', limit.toString());
    }

    return this.http
      .get(vehicle.statesUrl, {
        params: params,
      })
      .pipe(
        map((res: any) => res['results']),
        map((res): VehicleState[] => {
          return res.map((event: any) =>
            VehicleStateApiService.fromJSON(event),
          );
        }),
      );
  }

  load(state: VehicleState): Observable<VehicleState> {
    return this.http.get(state.detailUrl).pipe(
      map((res): VehicleState => {
        return VehicleStateApiService.fromJSON(res);
      }),
    );
  }

  getReadings(vehicleStates: VehicleState, next: any) {
    const url = next
      ? next
      : `${this.getBaseUrl()}/vehicle-states/${vehicleStates.vehicle}/${
          vehicleStates.id
        }/readings/`;
    return this.http.get(url);
  }

  // TODO: replace hand-rolled pagination loop with fetchAllPages (@fc-shared/utils/request-utils/fetch-all-pages)
  getAllReadings(vehicleStates: VehicleState) {
    return this.getReadings(vehicleStates, null).pipe(
      expand((obj: any) => {
        if (obj.next) {
          return this.getReadings(vehicleStates, obj.next);
        } else {
          return EMPTY;
        }
      }),
      map((obj: any) => obj.results.flatMap((array: any) => array)),
      reduce((acc, x) => acc.concat(x), []),
    );
  }

  /**
   * Route line of a state — `null` when the state has no line (a resting
   * state, or the road snap failed). `snapped` requests the road-following
   * map-matched variant; without it the backend returns the raw driven path,
   * one vertex per reading.
   */
  getPath(
    state: VehicleState,
    snapped: boolean,
  ): Observable<VehicleStatePath | null> {
    return this.http.get<VehicleStatePath | null>(
      `${this.getBaseUrl()}/vehicle-states/${state.vehicle}/${state.id}/path/`,
      { params: snapped ? { snapped: 'true' } : undefined },
    );
  }

  getVehicleStates(
    vehicleId: number,
    start: string,
    end: string,
  ): Observable<VehicleState[]> {
    const params: HttpParams = new HttpParams({
      encoder: new FCHttpParamEncoder(),
    })
      .append('time_frame', 'daily')
      .append('start', start)
      .append('end', end);

    return this.http
      .get<BackendResponse<VehicleState[]>>(
        `${this.getBaseUrl()}/vehicle-states/${vehicleId}/`,
        {
          params: params,
        },
      )
      .pipe(
        map((res: BackendResponse<VehicleState[]>): VehicleState[] =>
          res.results.map((event) => VehicleStateApiService.fromJSON(event)),
        ),
      );
  }

  loadByDate(
    vehicle: AnyVehicle,
    start: Moment,
    end?: Moment,
  ): Observable<VehicleState[]> {
    let params: HttpParams = new HttpParams({
      encoder: new FCHttpParamEncoder(),
    });
    if (start) params = params.append('start', start.format());
    if (end) params = params.append('end', end.format());

    if (!vehicle.statesUrl) return EMPTY;

    // TODO: replace hand-rolled pagination loop with fetchAllPages (@fc-shared/utils/request-utils/fetch-all-pages)
    return this.http
      .get<BackendResponse<VehicleState[]>>(vehicle.statesUrl, {
        params: params,
      })
      .pipe(
        expand((results: BackendResponse<VehicleState[]>) =>
          !!results.next
            ? this.http.get<BackendResponse<VehicleState[]>>(results.next)
            : EMPTY,
        ),
        reduce(
          (acc, resp: BackendResponse<VehicleState[]>) =>
            acc.concat(
              resp.results.map((event) =>
                VehicleStateApiService.fromJSON(event),
              ),
            ),
          [],
        ),
      );
  }

  getHistogram(
    vehicleId: number,
    upper: string,
    lower: string,
  ): Observable<{ [key: string]: VehicleStateHistorgamData }> {
    return this.http.get<{ [key: string]: VehicleStateHistorgamData }>(
      `${this.getBaseUrl()}/vehicle-states/${vehicleId}/histogram/?duration__range_upper=${upper}&duration__range_lower=${lower}`,
    );
  }
}
